// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/servicePages.ts
// 🔧 PURPOSE: I servizi della Home (consegna, auto, escursioni e il planner
//             su misura, massaggi, bici, emergenze, rifiuti, gioco) sono
//             pagine a tutto schermo, ognuna col suo indirizzo sotto
//             /services/... (prima erano finestre sopra la Home).
//
//             Qui, in un posto solo, quale servizio sta a quale indirizzo:
//             lo usano le rotte (App.tsx), openService() della Home
//             (ServiceDialogsHost) e le pagine stesse (src/pages/services).
//
//             I rifiuti hanno due varianti sulla stessa pagina: col ritiro
//             (ospite nel soggiorno) e la sola guida alla differenziata
//             (?view=guide, prima dell'arrivo o senza accesso).
//
//             useOpenServicePage() apre una pagina con un tocco solo: la
//             prima volta il suo pezzo si scarica e la schermata di prima
//             resta ferma; un secondo tocco aggiungeva un'altra voce, e "‹"
//             riportava alla stessa pagina.
//
//             Aperta da un foglio della Home ("Esperienze e noleggi"), la
//             pagina riscrive prima la voce della Home col segno del foglio
//             (?sheet=experiences, `backTo`): indietro si torna alla Home col
//             foglio aperto. La scheda Home della barra porta a "/", senza
//             segno: foglio chiuso.
// ============================================

import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** I servizi che openService() sa aprire. */
export type ServiceKind =
  | 'delivery'
  | 'rentCar'
  | 'excursions'
  | 'excursionPlanner'
  | 'massage'
  | 'rentBike'
  | 'emergency'
  | 'recycle'
  /** La stessa pagina dei rifiuti, ma solo la guida alla differenziata (niente ritiro). */
  | 'recycleGuide'
  | 'game'
  /** "Dal tarallo al trullo" (il 2048), solo dopo l'accesso. */
  | 'tarallo'
  /** "Spara Sapori" (il gioco delle bolle), solo dopo l'accesso. */
  | 'sapori'
  /** "Polpo Goloso" (il gioco del labirinto), solo dopo l'accesso. */
  | 'polpo';

/** Il percorso (senza query) della pagina di ogni servizio: le rotte di App.tsx. */
export const SERVICE_PATHS = {
  delivery: '/services/delivery',
  rentCar: '/services/rent-car',
  excursions: '/services/excursions',
  excursionPlanner: '/services/excursions/plan',
  massage: '/services/massage',
  rentBike: '/services/bike',
  emergency: '/services/emergency',
  recycle: '/services/recycling',
  game: '/services/game',
  tarallo: '/services/tarallo',
  sapori: '/services/spara-sapori',
  polpo: '/services/polpo-goloso',
} as const;

/** Il valore di ?view= che apre i rifiuti con la sola guida. */
export const RECYCLE_GUIDE_VIEW = 'guide';

/** L'indirizzo completo da aprire per un servizio (con la query, se serve). */
export function servicePath(kind: ServiceKind): string {
  if (kind === 'recycleGuide') return `${SERVICE_PATHS.recycle}?view=${RECYCLE_GUIDE_VIEW}`;
  return SERVICE_PATHS[kind];
}

/** La pagina dei rifiuti è stata aperta per la sola guida (?view=guide)? */
export function isRecycleGuideView(search: string): boolean {
  return new URLSearchParams(search).get('view') === RECYCLE_GUIDE_VIEW;
}

/** Il segno nell'indirizzo della Home: il foglio da riaprire tornando (?sheet=experiences). */
export const HOME_SHEET_PARAM = 'sheet';

/** Il foglio segnato nell'indirizzo (null: nessuno). */
export function homeSheetOf(search: string): string | null {
  return new URLSearchParams(search).get(HOME_SHEET_PARAM);
}

/** L'indirizzo con il segno del foglio `sheet` (o senza segno, con null). */
export function withHomeSheet(pathname: string, search: string, sheet: string | null): string {
  const params = new URLSearchParams(search);
  if (sheet) params.set(HOME_SHEET_PARAM, sheet);
  else params.delete(HOME_SHEET_PARAM);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export interface OpenServiceOptions {
  /**
   * La voce da cui si parte, riscritta (sostituita) prima di aprire: il "‹"
   * e il tasto indietro del telefono tornano lì. Es. la Home col foglio
   * aperto: withHomeSheet('/', '', 'experiences').
   */
  backTo?: string;
}

/**
 * Apre la pagina di un servizio (una voce nuova: indietro si torna qui, o a
 * `backTo`). Finché la pagina non è arrivata, gli altri tocchi non fanno
 * niente: neanche la voce di partenza si riscrive (sarebbe già la pagina).
 */
export function useOpenServicePage(): (kind: ServiceKind, options?: OpenServiceOptions) => void {
  const navigate = useNavigate();
  const { key } = useLocation();
  // La voce da cui è partita l'apertura in corso; arrivati altrove si libera.
  const leavingFrom = useRef<string | null>(null);
  useEffect(() => {
    leavingFrom.current = null;
  }, [key]);
  return useCallback(
    (kind: ServiceKind, options: OpenServiceOptions = {}) => {
      if (leavingFrom.current === key) return;
      leavingFrom.current = key;
      if (options.backTo) navigate(options.backTo, { replace: true });
      navigate(servicePath(kind));
    },
    [navigate, key],
  );
}
